/**
 * Reportes — port 1:1 desde Skeleton oficial (`/admin/reports`).
 *
 * Estructura (según screenshot del original):
 *   1. DateRangePicker: presets (Hoy, 7d, 30d, 90d) + 2 inputs date
 *   2. Análisis de Ventas: gráfica time-series con filtros (producto, día/semana/mes, ingresos/unidades, line/bar)
 *   3. Ventas por prenda (talla a talla): tabla colapsable con toggle paidOnly + agrupar por prenda + CSV
 *   4. 4 ReportCards (Ventas, Productos, Clientes, Inventario) con stats + Exportar CSV
 *
 * Llamadas:
 *   GET /api/v1/reports/sales|products|customers|inventory|product-sales|timeseries
 *   ?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD&format=csv (para descargar)
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import {
  DollarSign, Package, Users, Warehouse, Shirt, Download, ChevronDown, ChevronUp,
  TrendingUp, BarChart3,
} from 'lucide-react'
import { Card, Button, PageHeader, Input } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'

export const Route = createFileRoute('/store/$id/reports')({
  component: ReportsPage,
})

// ============================================================
// Helpers compartidos
// ============================================================

function todayYMD(): string {
  return new Date().toISOString().split('T')[0]!
}

function daysAgoYMD(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().split('T')[0]!
}

function formatColombianPrice(cents: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents)
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ============================================================
// Tipos
// ============================================================

interface SalesMetadata { totalRevenue: number; totalOrders: number; avgOrderValue: number }
interface ProductsMetadata { totalProducts: number; totalRevenue: number }
interface CustomersMetadata { totalCustomers: number; totalRevenue: number }
interface InventoryMetadata { totalItems: number; lowStockCount: number; outOfStockCount: number; totalUnits: number }
interface ProductSalesMetadata { totalUnits: number; totalRevenue: number; distinctProducts: number; rows: number }
interface ProductSalesRow {
  productId: string | null
  variantId: string | null
  productName: string
  size: string | null
  variantName: string | null
  unitsSold: number
  revenue: number
}

interface TimeseriesData {
  global: Array<{ date: string; revenue: number; units: number; orders: number }>
  byProduct: Array<{ date: string; productId: string; productName: string; revenue: number; units: number }>
  products: Array<{ id: string; name: string }>
}

// ============================================================
// Página principal
// ============================================================

function ReportsPage() {
  const { activeTenantId } = useAuth()
  const [startDate, setStartDate] = useState(daysAgoYMD(30))
  const [endDate, setEndDate] = useState(todayYMD())
  const [productSalesOpen, setProductSalesOpen] = useState(true)
  const [groupByProduct, setGroupByProduct] = useState(false)
  const [paidOnly, setPaidOnly] = useState(false)
  const [exporting, setExporting] = useState<Record<string, boolean>>({})

  const dateParams = `startDate=${startDate}&endDate=${endDate}`

  const sales = useQuery({
    queryKey: ['report-sales', startDate, endDate, activeTenantId],
    queryFn: () => tenantApi.get<{ data: { metadata: SalesMetadata } }>(`/api/v1/reports/sales?${dateParams}`),
    enabled: !!activeTenantId,
  })
  const productsR = useQuery({
    queryKey: ['report-products', startDate, endDate, activeTenantId],
    queryFn: () => tenantApi.get<{ data: { metadata: ProductsMetadata } }>(`/api/v1/reports/products?${dateParams}`),
    enabled: !!activeTenantId,
  })
  const customers = useQuery({
    queryKey: ['report-customers', startDate, endDate, activeTenantId],
    queryFn: () => tenantApi.get<{ data: { metadata: CustomersMetadata } }>(`/api/v1/reports/customers?${dateParams}`),
    enabled: !!activeTenantId,
  })
  const inventory = useQuery({
    queryKey: ['report-inventory', startDate, endDate, activeTenantId],
    queryFn: () => tenantApi.get<{ data: { metadata: InventoryMetadata } }>(`/api/v1/reports/inventory?${dateParams}`),
    enabled: !!activeTenantId,
  })
  const productSales = useQuery({
    queryKey: ['report-product-sales', startDate, endDate, paidOnly, activeTenantId],
    queryFn: () => tenantApi.get<{ data: { data: ProductSalesRow[]; metadata: ProductSalesMetadata } }>(
      `/api/v1/reports/product-sales?${dateParams}&paidOnly=${paidOnly}`,
    ),
    enabled: !!activeTenantId,
  })

  const initialLoading = sales.isLoading && productsR.isLoading && customers.isLoading && inventory.isLoading

  const productSalesData = productSales.data?.data?.data ?? []
  const productSalesMeta = productSales.data?.data?.metadata ?? { totalUnits: 0, totalRevenue: 0, distinctProducts: 0, rows: 0 }

  // Agrupar por producto sumando tallas si el toggle está activo
  const displayRows = useMemo(() => {
    if (!groupByProduct) return productSalesData
    const byProduct = new Map<string, ProductSalesRow & { sizes: Array<{ size: string | null; units: number }> }>()
    for (const r of productSalesData) {
      const key = r.productId || r.productName
      const existing = byProduct.get(key)
      if (existing) {
        existing.unitsSold += r.unitsSold
        existing.revenue += r.revenue
        existing.sizes.push({ size: r.size, units: r.unitsSold })
      } else {
        byProduct.set(key, { ...r, sizes: [{ size: r.size, units: r.unitsSold }] })
      }
    }
    return Array.from(byProduct.values()).sort((a, b) => b.unitsSold - a.unitsSold)
  }, [productSalesData, groupByProduct])

  async function handleExport(type: 'sales' | 'products' | 'product-sales' | 'customers' | 'inventory') {
    setExporting((e) => ({ ...e, [type]: true }))
    try {
      const params = new URLSearchParams({ format: 'csv', startDate, endDate })
      if (type === 'product-sales' && paidOnly) params.set('paidOnly', 'true')
      const csv = await tenantApi.getRaw(`/api/v1/reports/${type}?${params}`)
      downloadCSV(csv, `${type}-report-${startDate}-${endDate}.csv`)
    } catch (err) {
      console.error('export error', err)
    } finally {
      setExporting((e) => ({ ...e, [type]: false }))
    }
  }

  const salesMeta = sales.data?.data?.metadata
  const productsMeta = productsR.data?.data?.metadata
  const customersMeta = customers.data?.data?.metadata
  const inventoryMeta = inventory.data?.data?.metadata

  return (
    <>
      <PageHeader title="Reportes" description="Análisis de ventas, productos, clientes e inventario" />
      <div className="p-8 space-y-6">
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />

        <SalesTimeSeriesChart startDate={startDate} endDate={endDate} />

        {/* Ventas por prenda (talla a talla) */}
        <Card className="overflow-hidden">
          <button
            onClick={() => setProductSalesOpen((o) => !o)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-neutral-900/50 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <Shirt className="h-5 w-5 text-orange-400" />
              <div>
                <h2 className="text-sm font-semibold">Ventas por prenda {groupByProduct ? '' : '(talla a talla)'}</h2>
                <p className="text-xs text-neutral-500">
                  {productSalesMeta.distinctProducts || 0} prenda(s) — {productSalesMeta.totalUnits || 0} unidades — {formatColombianPrice(productSalesMeta.totalRevenue || 0)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                onClick={(e) => { e.stopPropagation(); setPaidOnly((p) => !p) }}
                className={cn(
                  'text-xs font-medium cursor-pointer px-2 py-1 rounded transition',
                  paidOnly ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'text-neutral-500 hover:text-white',
                )}
                title="Toggle: solo órdenes con pago aprobado"
              >
                {paidOnly ? '✓ Solo pagadas' : 'Todas las ventas'}
              </span>
              <span
                onClick={(e) => { e.stopPropagation(); setGroupByProduct((g) => !g) }}
                className="text-xs font-medium text-blue-400 hover:text-blue-300 cursor-pointer"
              >
                {groupByProduct ? 'Ver por talla' : 'Agrupar por prenda'}
              </span>
              <span
                onClick={(e) => { e.stopPropagation(); void handleExport('product-sales') }}
                className="inline-flex items-center gap-1 text-xs font-medium text-neutral-400 hover:text-white cursor-pointer"
                title="Descargar CSV"
              >
                <Download className="h-3.5 w-3.5" />CSV
              </span>
              {productSalesOpen ? <ChevronUp className="h-4 w-4 text-neutral-500" /> : <ChevronDown className="h-4 w-4 text-neutral-500" />}
            </div>
          </button>

          {productSalesOpen && (
            <div className="border-t border-neutral-800">
              {displayRows.length === 0 ? (
                <p className="px-6 py-8 text-center text-sm text-neutral-500">
                  No hay ventas en el rango seleccionado.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-950 text-xs text-neutral-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-3 text-left">Prenda</th>
                        <th className="px-6 py-3 text-left">{groupByProduct ? 'Desglose por talla' : 'Talla'}</th>
                        <th className="px-6 py-3 text-right">Unidades</th>
                        <th className="px-6 py-3 text-right">Ingresos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800">
                      {displayRows.map((r, idx) => (
                        <tr key={`${r.productId || r.productName}-${r.variantId || r.size || idx}`} className="hover:bg-neutral-900/50">
                          <td className="px-6 py-3 font-medium">{r.productName}</td>
                          <td className="px-6 py-3 text-neutral-300">
                            {groupByProduct && 'sizes' in r ? (
                              <div className="flex flex-wrap gap-1">
                                {(r as ProductSalesRow & { sizes: Array<{ size: string | null; units: number }> }).sizes.map((s, i) => (
                                  <span key={i} className="inline-flex items-center px-2 py-0.5 bg-neutral-800 rounded text-xs">
                                    {s.size || '—'}: <strong className="ml-1 text-white">{s.units}</strong>
                                  </span>
                                ))}
                              </div>
                            ) : r.size ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-xs font-medium">Talla {r.size}</span>
                            ) : (
                              <span className="text-xs text-red-400">Sin talla</span>
                            )}
                          </td>
                          <td className="px-6 py-3 text-right font-semibold tabular-nums">{r.unitsSold}</td>
                          <td className="px-6 py-3 text-right tabular-nums">{formatColombianPrice(r.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-neutral-950 border-t-2 border-neutral-700">
                      <tr>
                        <td className="px-6 py-3 font-bold" colSpan={2}>Total</td>
                        <td className="px-6 py-3 text-right font-bold tabular-nums">{productSalesMeta.totalUnits || 0} uds</td>
                        <td className="px-6 py-3 text-right font-bold tabular-nums">{formatColombianPrice(productSalesMeta.totalRevenue || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 4 ReportCards */}
        {initialLoading ? (
          <ReportCardsSkeleton />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ReportCard
              title="Ventas"
              description="Reporte de ventas y órdenes"
              icon={<DollarSign className="h-5 w-5 text-emerald-400" />}
              stats={[
                { label: 'Ingresos totales', value: formatColombianPrice(salesMeta?.totalRevenue ?? 0) },
                { label: 'Total órdenes', value: salesMeta?.totalOrders ?? 0 },
                { label: 'Ticket promedio', value: formatColombianPrice(salesMeta?.avgOrderValue ?? 0) },
              ]}
              onExport={() => handleExport('sales')}
              loading={exporting.sales}
            />
            <ReportCard
              title="Productos"
              description="Rendimiento de productos"
              icon={<Package className="h-5 w-5 text-blue-400" />}
              stats={[
                { label: 'Total productos', value: productsMeta?.totalProducts ?? 0 },
                { label: 'Ingresos', value: formatColombianPrice(productsMeta?.totalRevenue ?? 0) },
              ]}
              onExport={() => handleExport('products')}
              loading={exporting.products}
            />
            <ReportCard
              title="Clientes"
              description="Base de clientes"
              icon={<Users className="h-5 w-5 text-purple-400" />}
              stats={[
                { label: 'Total clientes', value: customersMeta?.totalCustomers ?? 0 },
                { label: 'Ingresos', value: formatColombianPrice(customersMeta?.totalRevenue ?? 0) },
              ]}
              onExport={() => handleExport('customers')}
              loading={exporting.customers}
            />
            <ReportCard
              title="Inventario"
              description="Estado del inventario"
              icon={<Warehouse className="h-5 w-5 text-orange-400" />}
              stats={[
                { label: 'Total items', value: inventoryMeta?.totalItems ?? 0 },
                { label: 'Stock bajo', value: inventoryMeta?.lowStockCount ?? 0 },
                { label: 'Sin stock', value: inventoryMeta?.outOfStockCount ?? 0 },
                { label: 'Unidades totales', value: inventoryMeta?.totalUnits ?? 0 },
              ]}
              onExport={() => handleExport('inventory')}
              loading={exporting.inventory}
            />
          </div>
        )}
      </div>
    </>
  )
}

// ============================================================
// DateRangePicker
// ============================================================

function DateRangePicker({
  startDate, endDate, onStartDateChange, onEndDateChange,
}: {
  startDate: string
  endDate: string
  onStartDateChange: (d: string) => void
  onEndDateChange: (d: string) => void
}) {
  const setPreset = (days: number) => {
    onStartDateChange(daysAgoYMD(days))
    onEndDateChange(todayYMD())
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex gap-1">
        <Button variant="outline" size="sm" onClick={() => setPreset(0)}>Hoy</Button>
        <Button variant="outline" size="sm" onClick={() => setPreset(7)}>7 días</Button>
        <Button variant="outline" size="sm" onClick={() => setPreset(30)}>30 días</Button>
        <Button variant="outline" size="sm" onClick={() => setPreset(90)}>90 días</Button>
      </div>
      <div className="flex items-center gap-2">
        <Input type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} className="w-auto text-sm" />
        <span className="text-neutral-500">—</span>
        <Input type="date" value={endDate} onChange={(e) => onEndDateChange(e.target.value)} className="w-auto text-sm" />
      </div>
    </div>
  )
}

// ============================================================
// SalesTimeSeriesChart
// ============================================================

const CHART_COLORS = [
  'hsl(217, 91%, 60%)', 'hsl(142, 71%, 45%)', 'hsl(0, 84%, 60%)', 'hsl(270, 70%, 60%)',
  'hsl(30, 90%, 55%)', 'hsl(180, 60%, 45%)', 'hsl(330, 70%, 55%)', 'hsl(50, 90%, 50%)',
]

function SalesTimeSeriesChart({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { activeTenantId } = useAuth()
  const [selectedProduct, setSelectedProduct] = useState<string>('all')
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day')
  const [metric, setMetric] = useState<'revenue' | 'units'>('revenue')
  const [chartType, setChartType] = useState<'line' | 'bar'>('line')

  const { data, isLoading } = useQuery({
    queryKey: ['report-timeseries', startDate, endDate, selectedProduct, groupBy, activeTenantId],
    queryFn: () => {
      const params = new URLSearchParams({ startDate, endDate, groupBy })
      if (selectedProduct !== 'all') params.set('productId', selectedProduct)
      return tenantApi.get<{ data: TimeseriesData }>(`/api/v1/reports/timeseries?${params}`)
    },
    enabled: !!activeTenantId,
  })

  const tsData = data?.data
  const products = tsData?.products ?? []

  const totalRevenue = (tsData?.global ?? []).reduce((s, r) => s + r.revenue, 0)
  const totalUnits = (tsData?.global ?? []).reduce((s, r) => s + r.units, 0)
  const totalOrders = (tsData?.global ?? []).reduce((s, r) => s + r.orders, 0)

  let formattedData: Array<Record<string, unknown>> = []
  let productNames: string[] = []

  if (selectedProduct === 'all' && tsData?.byProduct?.length) {
    const dateMap: Record<string, Record<string, unknown>> = {}
    const prodSet = new Set<string>()
    for (const row of tsData.byProduct) {
      if (!dateMap[row.date]) dateMap[row.date] = { date: row.date }
      prodSet.add(row.productName)
      dateMap[row.date]![row.productName] = metric === 'revenue' ? row.revenue : row.units
    }
    productNames = Array.from(prodSet).slice(0, 8)
    formattedData = Object.values(dateMap).sort((a, b) => String(a.date).localeCompare(String(b.date)))
  } else {
    formattedData = (tsData?.global ?? []).map((row) => ({
      date: row.date,
      valor: metric === 'revenue' ? row.revenue : row.units,
    }))
  }

  const formatDate = (date: string) => {
    const d = new Date(date + 'T00:00:00')
    if (groupBy === 'month') return d.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' })
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
  }
  const formatValue = (v: number) => metric === 'revenue' ? formatColombianPrice(v) : v.toString()

  const ChartComponent = chartType === 'bar' ? BarChart : LineChart

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-neutral-800 rounded w-48" />
          <div className="h-64 bg-neutral-900 rounded" />
        </div>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-400" />
            Análisis de Ventas
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">Comportamiento en el tiempo por producto</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-white font-semibold tabular-nums">{formatColombianPrice(totalRevenue)}</span>
          <span className="text-neutral-600">|</span>
          <span className="text-neutral-400 tabular-nums">{totalUnits} uds</span>
          <span className="text-neutral-600">|</span>
          <span className="text-neutral-400 tabular-nums">{totalOrders} órdenes</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          className="text-xs border border-neutral-800 rounded-lg px-3 py-1.5 bg-neutral-900 text-neutral-300 focus:outline-none focus:border-neutral-600"
        >
          <option value="all">Todos los productos</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <div className="flex border border-neutral-800 rounded-lg overflow-hidden">
          {(['day', 'week', 'month'] as const).map((g) => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                groupBy === g ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-neutral-400 hover:bg-neutral-800',
              )}
            >
              {g === 'day' ? 'Día' : g === 'week' ? 'Semana' : 'Mes'}
            </button>
          ))}
        </div>

        <div className="flex border border-neutral-800 rounded-lg overflow-hidden">
          {(['revenue', 'units'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                metric === m ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-neutral-400 hover:bg-neutral-800',
              )}
            >
              {m === 'revenue' ? 'Ingresos' : 'Unidades'}
            </button>
          ))}
        </div>

        <button
          onClick={() => setChartType((p) => p === 'line' ? 'bar' : 'line')}
          className="p-1.5 border border-neutral-800 rounded-lg hover:bg-neutral-900 transition"
          title={chartType === 'line' ? 'Cambiar a barras' : 'Cambiar a líneas'}
        >
          <BarChart3 className="h-4 w-4 text-neutral-400" />
        </button>
      </div>

      {/* Chart */}
      {formattedData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-neutral-500 text-sm">
          Sin datos para el período seleccionado
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <ChartComponent data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
            <XAxis dataKey="date" tickFormatter={(v) => formatDate(String(v))} fontSize={11} stroke="#737373" />
            <YAxis
              fontSize={11}
              stroke="#737373"
              tickFormatter={(v) => metric === 'revenue' ? `$${(Number(v) / 1000).toFixed(0)}K` : String(v)}
            />
            <Tooltip
              formatter={((value: unknown, name: unknown) => [formatValue(Number(value)), name === 'valor' ? (metric === 'revenue' ? 'Ingresos' : 'Unidades') : String(name)]) as never}
              labelFormatter={((label: unknown) => formatDate(String(label))) as never}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #262626', backgroundColor: '#0a0a0a' }}
              labelStyle={{ color: '#e5e5e5' }}
            />
            {productNames.length > 0 ? (
              <>
                <Legend />
                {productNames.map((name, i) => (
                  chartType === 'line' ? (
                    <Line key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
                  ) : (
                    <Bar key={name} dataKey={name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  )
                ))}
              </>
            ) : (
              chartType === 'line' ? (
                <Line type="monotone" dataKey="valor" stroke="hsl(217, 91%, 60%)" strokeWidth={2} dot={{ r: 3 }} />
              ) : (
                <Bar dataKey="valor" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
              )
            )}
          </ChartComponent>
        </ResponsiveContainer>
      )}
    </Card>
  )
}

// ============================================================
// ReportCard + Skeleton
// ============================================================

function ReportCard({
  title, description, icon, stats, onExport, loading,
}: {
  title: string
  description: string
  icon: React.ReactNode
  stats: Array<{ label: string; value: string | number }>
  onExport: () => void
  loading?: boolean
}) {
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-neutral-900 border border-neutral-800 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-neutral-500">{description}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-neutral-900 rounded p-2.5 border border-neutral-800">
            <p className="text-xs text-neutral-500">{s.label}</p>
            <p className="text-sm font-semibold tabular-nums">{s.value}</p>
          </div>
        ))}
      </div>
      <Button onClick={onExport} disabled={loading} variant="outline" className="w-full">
        <Download className="h-4 w-4 mr-2" />
        {loading ? 'Exportando...' : 'Exportar CSV'}
      </Button>
    </Card>
  )
}

function ReportCardsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-neutral-800" />
            <div className="space-y-2">
              <div className="h-4 w-24 bg-neutral-800 rounded" />
              <div className="h-3 w-40 bg-neutral-900 rounded" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="h-12 bg-neutral-900 rounded" />
            <div className="h-12 bg-neutral-900 rounded" />
          </div>
          <div className="h-9 bg-neutral-900 rounded" />
        </Card>
      ))}
    </div>
  )
}
